import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Upload, CheckCircle2, AlertCircle } from "lucide-react";
import { computeDol } from "@/lib/clinical";

const initial = {
  name: "", mother_name: "", father_name: "", dob: "", gender: "",
  birth_weight_g: "", current_weight_g: "", gestational_age_weeks: "",
  day_of_life: "", blood_group: "", address: "", phone: "",
  diagnosis: "", consultant: "", bed_number: "", nicu_area: "",
  referral: "", photo_file_id: null, document_file_ids: [],
  critical: false,
};

export default function Admission() {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [photoName, setPhotoName] = useState("");
  const [docNames, setDocNames] = useState([]);
  const [bedMap, setBedMap] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/beds").then(({ data }) => setBedMap(data || [])).catch(() => {});
  }, []);

  const bedByNumber = useMemo(() => {
    const m = {};
    for (const b of bedMap) m[b.bed_number] = b;
    return m;
  }, [bedMap]);
  const selectedBed = bedByNumber[f.bed_number];
  const vacantCount = bedMap.filter((b) => !b.occupied).length;

  const set = (k, v) => {
    setF((s) => {
      const next = { ...s, [k]: v };
      // Auto-fill DOL from DOB unless the user has already typed a DOL manually.
      if (k === "dob") {
        const dol = computeDol(v);
        if (dol != null && (s.day_of_life === "" || s._dol_auto)) {
          next.day_of_life = String(dol);
          next._dol_auto = true;
        }
      }
      if (k === "day_of_life") next._dol_auto = false;
      return next;
    });
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo_file_id", data.id);
      setPhotoName(file.name);
      toast.success("Photo uploaded");
    } catch (e) { toast.error("Photo upload failed"); }
  };

  const uploadDocs = async (files) => {
    const arr = Array.from(files || []);
    for (const file of arr) {
      const fd = new FormData(); fd.append("file", file);
      try {
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setF((s) => ({ ...s, document_file_ids: [...s.document_file_ids, data.id] }));
        setDocNames((s) => [...s, file.name]);
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name) { toast.error("Patient name required"); return; }
    setSaving(true);
    try {
      const payload = { ...f };
      ["birth_weight_g", "current_weight_g", "gestational_age_weeks", "day_of_life"].forEach((k) => {
        if (payload[k] === "" || payload[k] === null) delete payload[k]; else payload[k] = Number(payload[k]);
      });
      const { data } = await api.post("/patients", payload);
      toast.success(`Admitted — ${data.hospital_id}`);
      nav(`/patients/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Admission failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="admission-page">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Reception</div>
        <h1 className="font-display text-3xl font-semibold mt-1">New admission</h1>
        <p className="text-sm text-muted-foreground mt-1">Hospital ID auto-generated on save.</p>
      </div>

      <form onSubmit={submit} className="card-clinical p-6 lg:p-8 space-y-8">
        <Section title="Demographics">
          <Field label="Patient name *"><Input data-testid="adm-name" value={f.name} onChange={(e) => set("name", e.target.value)} required /></Field>
          <Field label="Mother's name"><Input value={f.mother_name} onChange={(e) => set("mother_name", e.target.value)} /></Field>
          <Field label="Father's name"><Input value={f.father_name} onChange={(e) => set("father_name", e.target.value)} /></Field>
          <Field label="Date of birth"><Input type="datetime-local" value={f.dob} onChange={(e) => set("dob", e.target.value)} /></Field>
          <Field label="Gender">
            <Select value={f.gender} onValueChange={(v) => set("gender", v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Male">Male</SelectItem>
                <SelectItem value="Female">Female</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Blood group"><Input value={f.blood_group} onChange={(e) => set("blood_group", e.target.value)} placeholder="e.g., O+" /></Field>
        </Section>

        <Section title="Neonatal parameters">
          <Field label="Birth weight (g)"><Input type="number" value={f.birth_weight_g} onChange={(e) => set("birth_weight_g", e.target.value)} /></Field>
          <Field label="Current weight (g)"><Input type="number" value={f.current_weight_g} onChange={(e) => set("current_weight_g", e.target.value)} /></Field>
          <Field label="Gestational age (weeks)"><Input type="number" step="0.1" value={f.gestational_age_weeks} onChange={(e) => set("gestational_age_weeks", e.target.value)} /></Field>
          <Field label={`Day of life${f._dol_auto ? " (auto from DOB)" : ""}`}>
            <Input
              type="number"
              value={f.day_of_life}
              onChange={(e) => set("day_of_life", e.target.value)}
              placeholder={f.dob ? "Auto from DOB — override if needed" : "e.g., 3"}
              data-testid="adm-dol"
            />
          </Field>
        </Section>

        <Section title="Clinical & bed">
          <Field label="Diagnosis" wide><Textarea rows={2} value={f.diagnosis} onChange={(e) => set("diagnosis", e.target.value)} placeholder="e.g., Preterm 32w, RDS, sepsis" /></Field>
          <Field label="Consultant"><Input data-testid="adm-consultant" value={f.consultant} onChange={(e) => set("consultant", e.target.value)} /></Field>
          <Field label="NICU area">
            <Select value={f.nicu_area} onValueChange={(v) => set("nicu_area", v)}>
              <SelectTrigger><SelectValue placeholder="Select area" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="NICU">NICU</SelectItem>
                <SelectItem value="Isolation">Isolation</SelectItem>
                <SelectItem value="Step-down">Step-down</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={`Bed number${bedMap.length ? ` — ${vacantCount} vacant / ${bedMap.length}` : ""}`}>
            <Select value={f.bed_number} onValueChange={(v) => set("bed_number", v)}>
              <SelectTrigger data-testid="adm-bed"><SelectValue placeholder="Select bed" /></SelectTrigger>
              <SelectContent>
                {(bedMap.length
                  ? bedMap
                  : Array.from({ length: 20 }, (_, i) => ({ bed_number: `Bed-${String(i + 1).padStart(2, "0")}`, occupied: false }))
                ).map((b) => (
                  <SelectItem key={b.bed_number} value={b.bed_number} disabled={b.occupied}>
                    <span className="inline-flex items-center gap-2">
                      <span className={`inline-block h-1.5 w-1.5 rounded-full ${b.occupied ? "bg-rose-500" : "bg-emerald-500"}`} />
                      <span>{b.bed_number}</span>
                      {b.occupied && (
                        <span className="text-[10px] text-muted-foreground">· {b.patient_name || "occupied"}</span>
                      )}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedBed && (
              selectedBed.occupied ? (
                <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-rose-600 dark:text-rose-300" data-testid="bed-occupied-hint">
                  <AlertCircle size={12} />
                  <span>Occupied by <b>{selectedBed.patient_name}</b> — pick another bed</span>
                </div>
              ) : (
                <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-emerald-600 dark:text-emerald-300" data-testid="bed-vacant-hint">
                  <CheckCircle2 size={12} />
                  <span>Vacant — ready to admit</span>
                </div>
              )
            )}
          </Field>
          <Field label="Referral"><Input value={f.referral} onChange={(e) => set("referral", e.target.value)} /></Field>
          <Field label="Critical">
            <div className="flex items-center gap-2 h-9">
              <input type="checkbox" checked={f.critical} onChange={(e) => set("critical", e.target.checked)} className="h-4 w-4 accent-red-500" data-testid="adm-critical" />
              <span className="text-sm text-muted-foreground">Mark this baby as critical</span>
            </div>
          </Field>
        </Section>

        <Section title="Contact">
          <Field label="Address" wide><Textarea rows={2} value={f.address} onChange={(e) => set("address", e.target.value)} /></Field>
          <Field label="Phone"><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
        </Section>

        <Section title="Photo & documents">
          <Field label="Baby photo">
            <label className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-dashed border-border cursor-pointer text-sm text-muted-foreground hover:text-foreground">
              <Upload size={14} /> {photoName || "Upload image"}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
            </label>
            {f.photo_file_id && <div className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1 mt-1"><CheckCircle2 size={12} /> uploaded</div>}
          </Field>
          <Field label="Reports / referral docs" wide>
            <label className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-dashed border-border cursor-pointer text-sm text-muted-foreground hover:text-foreground">
              <Upload size={14} /> Attach files
              <input type="file" multiple className="hidden" onChange={(e) => uploadDocs(e.target.files)} />
            </label>
            {docNames.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {docNames.map((n, i) => <span key={`${n}-${i}`} className="text-xs bg-muted rounded-full px-2 py-0.5">{n}</span>)}
              </div>
            )}
          </Field>
        </Section>

        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button type="button" variant="outline" onClick={() => setF(initial)}>Reset</Button>
          <Button type="submit" disabled={saving} data-testid="adm-submit" className="rounded-full px-6">
            {saving ? "Saving…" : "Admit patient"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-primary font-semibold mb-4">{title}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>
    </div>
  );
}
function Field({ label, wide, children }) {
  return (
    <div className={wide ? "md:col-span-2 lg:col-span-3 space-y-1.5" : "space-y-1.5"}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
